/**
 * The viewer's standing on a firm, derived from the perspective and their own memberships. Shared
 * by the firm Overview and the LAB Routing page so both describe the viewer in the same words.
 */
import type { FirmRef, ViewerContext, ViewerMembership } from './source';
import type { Standing } from './types';
import type { PermissionType } from '@thinkalpha/common/contracts/permissions.js';
import { doesAccessGrantAllowRequest } from '@thinkalpha/common/util/permissions.js';
import type { BadgeTone } from 'src/admin/components/markup/badge';

export const firmDisplayName = (firm: { name: string; displayName?: string }): string => firm.displayName || firm.name;

export const RUNG_LABEL: Record<PermissionType, string> = {
    reader: 'Reader',
    writer: 'Writer',
    owner: 'Owner',
    admin: 'Admin',
};

export const RUNG_TONE: Record<PermissionType, BadgeTone> = {
    reader: 'neutral',
    writer: 'neutral',
    owner: 'info',
    admin: 'accent',
};

export const rungLabel = (permission: PermissionType | null): string =>
    permission ? RUNG_LABEL[permission] : 'Member only';

export const rungTone = (permission: PermissionType | null): BadgeTone =>
    permission ? RUNG_TONE[permission] : 'neutral';

/** Owner is the rung at which a person may administer others. */
export const MANAGER_RUNG: PermissionType = 'owner';

export const isManagerRung = (permission: PermissionType | null): boolean =>
    permission !== null && doesAccessGrantAllowRequest(permission, MANAGER_RUNG);

/** The viewer's effective membership on a firm, if any is active. */
export function membershipOn(viewer: ViewerContext, firmId: string): ViewerMembership | undefined {
    return viewer.memberships.find((membership) => membership.firmId === firmId && membership.active);
}

/** The viewer's rung on a firm through their own membership, or null when none grants one. */
export function rungOn(viewer: ViewerContext, firmId: string): PermissionType | null {
    return membershipOn(viewer, firmId)?.permission ?? null;
}

/** Parents of the firm the viewer manages at owner or above. */
export function managedParents(viewer: ViewerContext, parents: FirmRef[]): FirmRef[] {
    return parents.filter((parent) => isManagerRung(rungOn(viewer, parent.id)));
}

/**
 * The viewer's standing on a firm, in the order the backend consults it: superuser perspective,
 * the viewer's own membership, then ownership of an immediate parent, which opens the firm but
 * grants nothing on what it owns.
 */
export function standingOn(
    viewer: ViewerContext,
    firm: { id: string; name: string; displayName?: string; parents: FirmRef[] },
    firmNameById: (id: string) => string,
): Standing {
    const name = firmDisplayName(firm);
    if (viewer.isSuperuser) {
        return {
            kind: 'superuser',
            label: 'Superuser',
            tone: 'accent',
            explanation: 'Superuser privileges are on and the backends answer without filtering.',
        };
    }
    const membership = membershipOn(viewer, firm.id);
    if (membership) {
        const dependsOn = membership.dependsOnFirmId ? firmNameById(membership.dependsOnFirmId) : undefined;
        const base = membership.permission
            ? `You are an ${RUNG_LABEL[membership.permission].toLowerCase()} of ${name}, so you hold that rung on everything the firm owns.`
            : `You belong to ${name} and receive its applied roles, but you don’t have a rung on its accounts or other resources.`;
        const dependency = dependsOn
            ? ` This membership depends on your membership in ${dependsOn}: it was created from there and goes dark if that one is suspended.`
            : '';
        return {
            kind: 'member',
            label: rungLabel(membership.permission),
            tone: rungTone(membership.permission),
            explanation: base + dependency,
            dependsOn,
        };
    }
    const parents = managedParents(viewer, firm.parents);
    if (parents.length > 0) {
        const parentNames = parents.map(firmDisplayName).join(' and ');
        return {
            kind: 'parent-authority',
            label: 'Via parent',
            tone: 'warning',
            explanation: `You administer ${name} as an owner of ${parentNames}. You can open it and add roles or members to it. You don’t get a rung on its accounts or members until you join it.`,
        };
    }
    return {
        kind: 'none',
        label: 'No access',
        tone: 'neutral',
        explanation: `You don’t belong to ${name} and you own none of its parents.`,
    };
}

/** "Viewing as owner of Wedbush Securities, a parent of Cobra Trading". */
export function perspectiveOn(
    viewer: ViewerContext,
    firm: { id: string; name: string; displayName?: string; parents: FirmRef[] },
    firmNameById: (id: string) => string,
): string {
    if (viewer.isSuperuser) return 'Viewing as superuser';
    const name = firmDisplayName(firm);
    const membership = membershipOn(viewer, firm.id);
    if (membership) {
        const rung = rungLabel(membership.permission).toLowerCase();
        const dependency = membership.dependsOnFirmId
            ? ` (membership depends on ${firmNameById(membership.dependsOnFirmId)})`
            : '';
        return `Viewing as ${rung} of ${name}${dependency}`;
    }
    const parents = managedParents(viewer, firm.parents);
    if (parents.length > 0) {
        const parent = parents[0]!;
        const rung = rungLabel(rungOn(viewer, parent.id)).toLowerCase();
        return `Viewing as ${rung} of ${firmDisplayName(parent)}, a parent of ${name}`;
    }
    return `Viewing with no standing on ${name}`;
}

/** "Viewing as owner of Wedbush Securities and Meridian Day Trading" for pages without a focal firm. */
export function perspectiveAcross(viewer: ViewerContext, firmNameById: (id: string) => string): string {
    if (viewer.isSuperuser) return 'Viewing as superuser';
    const managed = viewer.memberships.filter((m) => m.active && isManagerRung(m.permission));
    if (managed.length === 0) return 'Viewing with no firm you administer';
    const byRung = new Map<string, string[]>();
    for (const membership of managed) {
        const rung = rungLabel(membership.permission).toLowerCase();
        byRung.set(rung, [...(byRung.get(rung) ?? []), firmNameById(membership.firmId)]);
    }
    return `Viewing as ${[...byRung].map(([rung, names]) => `${rung} of ${names.join(' and ')}`).join(', ')}`;
}
